<template>
  <Drawer :visible.sync="isVisible" :withHeader="false" :append-to-body="true" direction="rtl" size="500px"
    @open="fetchExistingCategorization" @close="handleCancel" :before-close="handleCancel" class="default-drawer">
    <div class="sdlc-categorization-popup">
      <div class="sdlc-popup-header">
        <div class="text-blue-dark1 text-md">
          <span>{{ $t('MATERIAL_CATEGORIZATION') }}</span>
        </div>
        <div class="sdlc-popup-actions">
          <div class="save-button-container">
            <Button type="primary" @click="handleSave" :loading="isSaving"
              :disabled="isSaving || isLoading || !canEdit">
              {{ $t('SAVE') }}
            </Button>
          </div>
          <div class="header-action">
            <button type="btn" class="btn section_obtn" @click="handleCancel">
              {{ $t('CANCEL') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="isLoading" style="height: 30px; align-items:center">
        <Spinner />
      </div>

      <!-- Content with Collapse sections -->
      <div v-else-if="material" class="collapse-slider rfq-empty-state mx-15">
        <Collapse v-model="activeCategories" class="collapse-slider mx-15 pl-10">
          <!-- Shortage -->
          <CollapseItem name="shortage" class="details-tab-content">
            <template slot="title">
              <div class="flex-container">
                <span>{{ $t('SHORTAGE') }}</span>
                <span v-if="material && material.epod" class="epod-count">{{ formatEpodCount(categorizedCounts.shortage) }}</span>
              </div>
            </template>
            <div class="popscroll">
              <div v-if="canEdit && Number(categorizedCounts.shortage) !== Number(material.shortage_count)" class="add-block add_new" @click="addReason('shortage')"> {{ $t('ADD_SHORTAGE') }}
              </div>
              <div v-for="(item, index) in categorization.shortage" :key="`shortage-${index}`"
                class="reason-card shortage-card">
                <div class="card-header">
                  <span class="card-title">{{ $t('NEW_SHORTAGE') }}</span>
                  <div class="card-actions">
                    <svgicon name="icon-delete-md" class="pointer" width="14" height="14"
                      @click="removeReason('shortage', index)" />
                  </div>
                </div>
                <Form class="form-default mr-5 no-gap-form">
                  <FormItem :label="$t('SHORTAGE_QTY')">
                    <div class="count-unit-row">
                      <Input :placeholder="$t('ENTER_COUNT')" type="number" v-model.number="item.count"
                        class="count-input" />
                      <Select v-model="item.unit" class="unit-select">
                        <Option value="cases" :label="$t('CASES')" />
                        <Option value="pieces" :label="$t('PIECES')" />
                      </Select>
                    </div>
                  </FormItem>
                  <FormItem :label="$t('REASON')">
                    <Select class="select-default no-bg" v-model="item.category_reason"
                      :placeholder="$t('SELECT_REASON')" @change="resetProbableCause(item)">
                      <Option v-for="reason in reasonsByCategory.shortage" :key="reason" :label="reason" :value="reason" />
                    </Select>
                  </FormItem>
                  <FormItem v-if="item.category_reason" :label="$t('PROBABLE_CAUSE')">
                    <Select v-if="item.category_reason !== 'Others' && getSubReasons('shortage', item.category_reason).length > 0"
                      class="select-default no-bg" v-model="item.probable_cause" :placeholder="$t('PROBABLE_CAUSE')">
                      <Option v-for="cause in getSubReasons('shortage', item.category_reason)" :key="cause" :label="cause" :value="cause" />
                      <Option value="Others" :label="$t('OTHERS')" />
                    </Select>
                    <Input v-else :placeholder="$t('PROBABLE_CAUSE_REASON')" v-model="item.probable_cause" :maxlength="150" />
                  </FormItem>
                  <FormItem v-if="item.category_reason && item.probable_cause === 'Others'" :label="$t('PROBABLE_CAUSE_REASON')">
                    <Input :placeholder="$t('PROBABLE_CAUSE_REASON')" v-model="item.probable_cause_text" :maxlength="150" />
                  </FormItem>
                </Form>
              </div>
              <!-- Existing items -->
              <div v-for="(item, index) in existingCategorization.shortage" :key="`shortage-existing-${index}`"
                class="summary-card">
                <div class="summary-card-content">
                  <div class="summary-field">
                    <span class="summary-label">{{ $t('SHORTAGE') }}</span>
                    <span class="summary-value">{{ formatStoredCount(item) }}</span>
                  </div>
                  <div class="summary-field">
                    <span class="summary-label">{{ $t('REASON') }}</span>
                    <span class="summary-value ellipsis">{{ item.category_reason }}</span>
                  </div>
                  <div v-if="item.probable_cause" class="summary-field">
                    <span class="summary-label">{{ $t('PROBABLE_CAUSE') }}</span>
                    <span class="summary-value ellipsis">{{ item.probable_cause }}</span>
                  </div>
                  <div v-if="item.reason" class="summary-field">
                    <span class="summary-label">{{ $t('PROBABLE_CAUSE_REASON') }}</span>
                    <span class="summary-value ellipsis">{{ item.reason }}</span>
                  </div>
                </div>
              </div>
            </div>
          </CollapseItem>

          <!-- Damage -->
          <CollapseItem name="damage" class="actions-tab-content">
            <template slot="title">
              <div class="flex-container">
                <span>{{ $t('DAMAGE') }}</span>
                <span v-if="material && material.epod" class="epod-count">
                  {{ formatEpodCount(categorizedCounts.damage) }}
                </span>
              </div>
            </template>
            <div class="popscroll">
              <div v-if="canEdit && Number(categorizedCounts.damage) !== Number(material.damage_count)" class="add-block add_new" @click="addReason('damage')"> {{ $t('ADD_DAMAGE') }}</div>
              <div v-for="(item, index) in categorization.damage" :key="`damage-${index}`"
                class="reason-card shortage-card">
                <div class="card-header">
                  <span class="card-title">{{ $t('NEW_DAMAGE') }}</span>
                  <div class="card-actions">
                    <svgicon name="icon-delete-md" class="pointer" width="14" height="14"
                      @click="removeReason('damage', index)" />
                  </div>
                </div>
                <Form class="form-default mr-5 no-gap-form">
                  <FormItem :label="$t('DAMAGE_QTY')">
                    <div class="count-unit-row">
                      <Input :placeholder="$t('ENTER_COUNT')" type="number" v-model.number="item.count"
                        class="count-input" />
                      <Select v-model="item.unit" class="unit-select">
                        <Option value="cases" :label="$t('CASES')" />
                        <Option value="pieces" :label="$t('PIECES')" />
                      </Select>
                    </div>
                  </FormItem>
                  <FormItem :label="$t('REASON')">
                    <Select class="select-default no-bg" v-model="item.category_reason"
                      :placeholder="$t('SELECT_REASON')" @change="resetProbableCause(item)">
                      <Option v-for="reason in reasonsByCategory.damage" :key="reason" :label="reason" :value="reason" />
                    </Select>
                  </FormItem>
                  <FormItem v-if="item.category_reason" :label="$t('PROBABLE_CAUSE')">
                    <Select v-if="item.category_reason !== 'Others' && getSubReasons('damage', item.category_reason).length > 0"
                      class="select-default no-bg" v-model="item.probable_cause" :placeholder="$t('PROBABLE_CAUSE')">
                      <Option v-for="cause in getSubReasons('damage', item.category_reason)" :key="cause" :label="cause" :value="cause" />
                      <Option value="Others" :label="$t('OTHERS')" />
                    </Select>
                    <Input v-else :placeholder="$t('PROBABLE_CAUSE_REASON')" v-model="item.probable_cause" :maxlength="150" />
                  </FormItem>
                  <FormItem v-if="item.category_reason && item.probable_cause === 'Others'" :label="$t('PROBABLE_CAUSE_REASON')">
                    <Input :placeholder="$t('PROBABLE_CAUSE_REASON')" v-model="item.probable_cause_text" :maxlength="150" />
                  </FormItem>
                </Form>
              </div>
              <!-- Existing items -->
              <div v-for="(item, index) in existingCategorization.damage" :key="`damage-existing-${index}`"
                class="summary-card">
                <div class="summary-card-content">
                  <div class="summary-field">
                    <span class="summary-label">{{ $t('DAMAGE') }}</span>
                    <span class="summary-value">{{ formatStoredCount(item) }}</span>
                  </div>
                  <div class="summary-field">
                    <span class="summary-label">{{ $t('REASON') }}</span>
                    <span class="summary-value ellipsis">{{ item.category_reason }}</span>
                  </div>
                  <div v-if="item.probable_cause" class="summary-field">
                    <span class="summary-label">{{ $t('PROBABLE_CAUSE') }}</span>
                    <span class="summary-value ellipsis">{{ item.probable_cause }}</span>
                  </div>
                  <div v-if="item.reason" class="summary-field">
                    <span class="summary-label">{{ $t('PROBABLE_CAUSE_REASON') }}</span>
                    <span class="summary-value ellipsis">{{ item.reason }}</span>
                  </div>
                </div>
              </div>
            </div>
          </CollapseItem>

          <!-- Carton Damage -->
          <CollapseItem name="carton_damage" class="actions-tab-content">
            <template slot="title">
              <div class="flex-container">
                <span>{{ $t('CARTON_DAMAGE') }}</span>
                <span v-if="material && material.epod" class="epod-count">
                  {{ formatEpodCount(categorizedCounts.carton_damage) }}
                </span>
              </div>
            </template>
            <div class="popscroll">
              <div v-if="canEdit && Number(categorizedCounts.carton_damage) !== Number(material.carton_damage_count)" class="add-block add_new" @click="addReason('carton_damage')"> {{
                $t('ADD_CARTON_DAMAGE') }}
              </div>
              <div v-for="(item, index) in categorization.carton_damage" :key="`carton_damage-${index}`"
                class="reason-card shortage-card">
                <div class="card-header">
                  <span class="card-title">{{ $t('NEW_CARTON_DAMAGE') }}</span>
                  <div class="card-actions">
                    <svgicon name="icon-delete-md" class="pointer" width="14" height="14"
                      @click="removeReason('carton_damage', index)" />
                  </div>
                </div>
                <Form class="form-default mr-5 no-gap-form">
                  <FormItem :label="$t('CARTON_DAMAGE_QTY')">
                    <div class="count-unit-row">
                      <Input :placeholder="$t('ENTER_COUNT')" type="number" v-model.number="item.count"
                        class="count-input" />
                      <Select v-model="item.unit" class="unit-select">
                        <Option value="cases" :label="$t('CASES')" />
                        <Option value="pieces" :label="$t('PIECES')" />
                      </Select>
                    </div>
                  </FormItem>
                  <FormItem :label="$t('REASON')">
                    <Select class="select-default no-bg" v-model="item.category_reason"
                      :placeholder="$t('SELECT_REASON')" @change="resetProbableCause(item)">
                      <Option v-for="reason in reasonsByCategory.carton_damage" :key="reason" :label="reason" :value="reason" />
                    </Select>
                  </FormItem>
                  <FormItem v-if="item.category_reason" :label="$t('PROBABLE_CAUSE')">
                    <Select v-if="item.category_reason !== 'Others' && getSubReasons('carton_damage', item.category_reason).length > 0"
                      class="select-default no-bg" v-model="item.probable_cause" :placeholder="$t('PROBABLE_CAUSE')">
                      <Option v-for="cause in getSubReasons('carton_damage', item.category_reason)" :key="cause" :label="cause" :value="cause" />
                      <Option value="Others" :label="$t('OTHERS')" />
                    </Select>
                    <Input v-else :placeholder="$t('PROBABLE_CAUSE_REASON')" v-model="item.probable_cause" :maxlength="150" />
                  </FormItem>
                  <FormItem v-if="item.category_reason && item.probable_cause === 'Others'" :label="$t('PROBABLE_CAUSE_REASON')">
                    <Input :placeholder="$t('PROBABLE_CAUSE_REASON')" v-model="item.probable_cause_text" :maxlength="150" />
                  </FormItem>
                </Form>
              </div>
              <!-- Existing items -->
              <div v-for="(item, index) in existingCategorization.carton_damage"
                :key="`carton_damage-existing-${index}`" class="summary-card">
                <div class="summary-card-content">
                  <div class="summary-field">
                    <span class="summary-label">{{ $t('CARTON_DAMAGE') }}</span>
                    <span class="summary-value">{{ formatStoredCount(item) }}</span>
                  </div>
                  <div class="summary-field">
                    <span class="summary-label">{{ $t('REASON') }}</span>
                    <span class="summary-value ellipsis">{{ item.category_reason }}</span>
                  </div>
                  <div v-if="item.probable_cause" class="summary-field">
                    <span class="summary-label">{{ $t('PROBABLE_CAUSE') }}</span>
                    <span class="summary-value ellipsis">{{ item.probable_cause }}</span>
                  </div>
                  <div v-if="item.reason" class="summary-field">
                    <span class="summary-label">{{ $t('PROBABLE_CAUSE_REASON') }}</span>
                    <span class="summary-value ellipsis">{{ item.reason }}</span>
                  </div>
                </div>
              </div>
            </div>
          </CollapseItem>

          <!-- Leakage -->
          <CollapseItem name="leakage" class="actions-tab-content" v-if="$hasFeature('show_grn_excess')">
            <template slot="title">
              <div class="flex-container">
                <span>{{ $t('LEAKAGE') }}</span>
                <span v-if="material && material.epod" class="epod-count">
                  {{ formatEpodCount(categorizedCounts.leakage) }}
                </span>
              </div>
            </template>
            <div class="popscroll">
              <div v-if="canEdit && Number(categorizedCounts.leakage) !== Number(material.leakage_count)" class="add-block add_new" @click="addReason('leakage')"> {{ $t('ADD_LEAKAGE') }}</div>
              <div v-for="(item, index) in categorization.leakage" :key="`leakage-${index}`"
                class="reason-card shortage-card">
                <div class="card-header">
                  <span class="card-title">{{ $t('NEW_LEAKAGE') }}</span>
                  <div class="card-actions">
                    <svgicon name="icon-delete-md" class="pointer" width="14" height="14"
                      @click="removeReason('leakage', index)" />
                  </div>
                </div>
                <Form class="form-default mr-5 no-gap-form">
                  <FormItem :label="$t('LEAKAGE_QTY')">
                    <div class="count-unit-row">
                      <Input :placeholder="$t('ENTER_COUNT')" type="number" v-model.number="item.count"
                        class="count-input" />
                      <Select v-model="item.unit" class="unit-select">
                        <Option value="cases" :label="$t('CASES')" />
                        <Option value="pieces" :label="$t('PIECES')" />
                      </Select>
                    </div>
                  </FormItem>
                  <FormItem :label="$t('REASON')">
                    <Select class="select-default no-bg" v-model="item.category_reason"
                      :placeholder="$t('SELECT_REASON')" @change="resetProbableCause(item)">
                      <Option v-for="reason in reasonsByCategory.leakage" :key="reason" :label="reason" :value="reason" />
                    </Select>
                  </FormItem>
                  <FormItem v-if="item.category_reason" :label="$t('PROBABLE_CAUSE')">
                    <Select v-if="item.category_reason !== 'Others' && getSubReasons('leakage', item.category_reason).length > 0"
                      class="select-default no-bg" v-model="item.probable_cause" :placeholder="$t('PROBABLE_CAUSE')">
                      <Option v-for="cause in getSubReasons('leakage', item.category_reason)" :key="cause" :label="cause" :value="cause" />
                      <Option value="Others" :label="$t('OTHERS')" />
                    </Select>
                    <Input v-else :placeholder="$t('PROBABLE_CAUSE_REASON')" v-model="item.probable_cause" :maxlength="150" />
                  </FormItem>
                  <FormItem v-if="item.category_reason && item.probable_cause === 'Others'" :label="$t('PROBABLE_CAUSE_REASON')">
                    <Input :placeholder="$t('PROBABLE_CAUSE_REASON')" v-model="item.probable_cause_text" :maxlength="150" />
                  </FormItem>
                </Form>
              </div>
              <!-- Existing items -->
              <div v-for="(item, index) in existingCategorization.leakage" :key="`leakage-existing-${index}`"
                class="summary-card">
                <div class="summary-card-content">
                  <div class="summary-field">
                    <span class="summary-label">{{ $t('LEAKAGE') }}</span>
                    <span class="summary-value">{{ formatStoredCount(item) }}</span>
                  </div>
                  <div class="summary-field">
                    <span class="summary-label">{{ $t('REASON') }}</span>
                    <span class="summary-value ellipsis">{{ item.category_reason }}</span>
                  </div>
                  <div v-if="item.probable_cause" class="summary-field">
                    <span class="summary-label">{{ $t('PROBABLE_CAUSE') }}</span>
                    <span class="summary-value ellipsis">{{ item.probable_cause }}</span>
                  </div>
                  <div v-if="item.reason" class="summary-field">
                    <span class="summary-label">{{ $t('PROBABLE_CAUSE_REASON') }}</span>
                    <span class="summary-value ellipsis">{{ item.reason }}</span>
                  </div>
                </div>
              </div>
            </div>
          </CollapseItem>

          <!-- Excess -->
          <CollapseItem name="excess" class="actions-tab-content" v-if="$hasFeature('show_grn_excess')">
            <template slot="title">
              <div class="flex-container">
                <span>{{ $t('EXCESS') }}</span>
                <span v-if="material && material.epod" class="epod-count">
                  {{ formatEpodCount(categorizedCounts.excess) }}
                </span>
              </div>
            </template>
            <div class="popscroll">
              <div v-if="canEdit && Number(categorizedCounts.excess) !== Number(material.excess_count)" class="add-block add_new" @click.stop="addReason('excess')">
                {{ $t('ADD_EXCESS') }}
              </div>
              <div v-for="(item, index) in categorization.excess" :key="`excess-${index}`"
                class="reason-card shortage-card">
                <div class="card-header">
                  <span class="card-title">{{ $t('NEW_EXCESS') }}</span>
                  <div class="card-actions">
                    <svgicon name="icon-delete-md" class="pointer" width="14" height="14"
                      @click="removeReason('excess', index)" />
                  </div>
                </div>
                <Form class="form-default mr-5 no-gap-form">
                  <FormItem :label="$t('EXCESS_QTY')">
                    <div class="count-unit-row">
                      <Input :placeholder="$t('ENTER_COUNT')" type="number" v-model.number="item.count"
                        class="count-input" />
                      <Select v-model="item.unit" class="unit-select">
                        <Option value="cases" :label="$t('CASES')" />
                        <Option value="pieces" :label="$t('PIECES')" />
                      </Select>
                    </div>
                  </FormItem>
                  <FormItem :label="$t('REASON')">
                    <Select class="select-default no-bg" v-model="item.category_reason"
                      :placeholder="$t('SELECT_REASON')" @change="resetProbableCause(item)">
                      <Option v-for="reason in reasonsByCategory.excess" :key="reason" :label="reason" :value="reason" />
                    </Select>
                  </FormItem>
                  <FormItem v-if="item.category_reason" :label="$t('PROBABLE_CAUSE')">
                    <Select v-if="item.category_reason !== 'Others' && getSubReasons('excess', item.category_reason).length > 0"
                      class="select-default no-bg" v-model="item.probable_cause" :placeholder="$t('PROBABLE_CAUSE')">
                      <Option v-for="cause in getSubReasons('excess', item.category_reason)" :key="cause" :label="cause" :value="cause" />
                      <Option value="Others" :label="$t('OTHERS')" />
                    </Select>
                    <Input v-else :placeholder="$t('PROBABLE_CAUSE_REASON')" v-model="item.probable_cause" :maxlength="150" />
                  </FormItem>
                  <FormItem v-if="item.category_reason && item.probable_cause === 'Others'" :label="$t('PROBABLE_CAUSE_REASON')">
                    <Input :placeholder="$t('PROBABLE_CAUSE_REASON')" v-model="item.probable_cause_text" :maxlength="150" />
                  </FormItem>
                </Form>
              </div>
              <!-- Existing items -->
              <div v-for="(item, index) in existingCategorization.excess" :key="`excess-existing-${index}`"
                class="summary-card">
                <div class="summary-card-content">
                  <div class="summary-field">
                    <span class="summary-label">{{ $t('EXCESS') }}</span>
                    <span class="summary-value">{{ formatStoredCount(item) }}</span>
                  </div>
                  <div class="summary-field">
                    <span class="summary-label">{{ $t('REASON') }}</span>
                    <span class="summary-value ellipsis">{{ item.category_reason }}</span>
                  </div>
                  <div v-if="item.probable_cause" class="summary-field">
                    <span class="summary-label">{{ $t('PROBABLE_CAUSE') }}</span>
                    <span class="summary-value ellipsis">{{ item.probable_cause }}</span>
                  </div>
                  <div v-if="item.reason" class="summary-field">
                    <span class="summary-label">{{ $t('PROBABLE_CAUSE_REASON') }}</span>
                    <span class="summary-value ellipsis">{{ item.reason }}</span>
                  </div>
                </div>
              </div>
            </div>
          </CollapseItem>
        </Collapse>
      </div>
    </div>
  </Drawer>
</template>

<script>
import { sdlcCategorizationUnits } from 'pando-constants';

export default {
  name: 'SdlcCategorizationPopup',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    orderNo: {
      type: String,
      default: '',
    },
    movement: {
      type: Number,
      default: null,
    },
    material: {
      type: Object,
      default: () => null,
    },
    orderStatus: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      activeCategories: [],
      reasons: [],
      reasonsByCategory: {
        shortage: ['Others'],
        damage: ['Others'],
        carton_damage: ['Others'],
        leakage: ['Others'],
        excess: ['Others'],
      },
      subReasonsByCategory: {
        shortage: {},
        damage: {},
        carton_damage: {},
        leakage: {},
        excess: {},
      },
      isLoading: false,
      isSaving: false,
      categorization: {
        shortage: [],
        damage: [],
        carton_damage: [],
        leakage: [],
        excess: [],
      },
      existingCategorization: {
        shortage: [],
        damage: [],
        carton_damage: [],
        leakage: [],
        excess: [],
      },
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    canEdit() {
      return this.$hasPermission('edit_sdlc_categorization') && !this.$statusUtil.isDelivered(this.orderStatus);
    },
    categorizedCounts() {
      const counts = { shortage: 0, damage: 0, carton_damage: 0, leakage: 0, excess: 0 };
      Object.keys(counts).forEach(category => {
        // Sum existing saved categorizations
        const existingItems = this.existingCategorization[category] || [];
        const existingTotal = existingItems.reduce((sum, item) => sum + Number(item.count || 0), 0);

        // Sum new unsaved categorizations (convert to fractional cases)
        const newItems = this.categorization[category] || [];
        const newTotal = newItems.reduce((sum, item) => sum + this.convertToFractionalCases(item), 0);

        counts[category] = existingTotal + newTotal;
      });
      return counts;
    },
  },

  methods: {
    async fetchExistingCategorization() {
      this.isLoading = true;
      try {
        await this.fetchReasons();
        const params = {
          orderNo: this.orderNo,
          materialRefId: this.material.material_ref_id,
          material_id: this.material.id,
          material_code: this.material.material.code,
        };

        if (this.material.source_location_id) {
          params.sourceLocationId = this.material.source_location_id;
        }
        if (this.material.destination_location_id) {
          params.destinationLocationId = this.material.destination_location_id;
        }
        if (this.material.material_invoice_no) {
          params.materialInvoiceNo = this.material.material_invoice_no;
        }
        if (this.material.line_item_no) {
          params.lineItemNo = this.material.line_item_no;
        }
        const response = await this.$store.dispatch('order/getSdlcCategorization', params);

        if (response && response.data && response.data.length > 0) {
          const existingData = response.data[0];
          this.existingCategorization = {
            shortage: existingData.shortage || [],
            damage: existingData.damage || [],
            carton_damage: existingData.carton_damage || [],
            leakage: existingData.leakage || [],
            excess: existingData.excess || [],
          };
        } else {
          this.resetExistingCategorization();
        }
      } catch (error) {
        this.resetExistingCategorization();
      } finally {
        this.isLoading = false;
      }
    },

    resetExistingCategorization() {
      this.existingCategorization = {
        shortage: [],
        damage: [],
        carton_damage: [],
        leakage: [],
        excess: [],
      };
    },

    async fetchReasons() {
      const subTypeCategories = ['shortage', 'damage', 'carton_damage', 'leakage', 'excess'];
      try {
        const results = await Promise.all(
          subTypeCategories.map(subType => this.$store.dispatch('settings/getReasonSettings', {
            depot_id: this.material.source_location_id,
            movement: this.movement,
            type: 'DISCREPANCY_CATEGORY',
            sub_type: subType,
          }))
        );
        subTypeCategories.forEach((category, index) => {
          const data = results[index] && results[index].data;
          if (data) {
            const reasons = Array.isArray(data) ? data : (data.reasons || []);
            const subReasons = Array.isArray(data) ? {} : (data.sub_reasons || {});
            if (reasons.length > 0) {
              this.reasonsByCategory[category] = [...reasons, 'Others'];
            }
            this.subReasonsByCategory[category] = subReasons;
          }
        });
        this.reasons = this.reasonsByCategory.shortage || ['Others'];
      } catch {
        const fallback = ['Others'];
        this.reasons = fallback;
        this.reasonsByCategory = {
          shortage: fallback, damage: fallback, carton_damage: fallback,
          leakage: fallback, excess: fallback,
        };
      }
    },

    getSubReasons(category, categoryReason) {
      if (!categoryReason || categoryReason === 'Others') return [];
      return (this.subReasonsByCategory[category] && this.subReasonsByCategory[category][categoryReason]) || [];
    },

    addReason(category) {
      this.categorization[category].push({
        category_reason: '',
        count: 0,
        unit: sdlcCategorizationUnits.CASES,
        probable_cause: '',
        probable_cause_text: '',
      });
    },
    removeReason(category, index) {
      this.categorization[category].splice(index, 1);
    },
    resetProbableCause(item) {
      item.probable_cause = '';
      item.probable_cause_text = '';
    },


    convertToFractionalCases(item) {
      // Convert count to fractional cases based on unit
      if (item.unit === sdlcCategorizationUnits.PIECES && this.material && this.material.
        piece_per_box) {
        return Number(item.count) / Number(this.material.piece_per_box);
      }
      return Number(item.count);
    },

    formatStoredCount(item) {
      // Display stored count with unit
      if (!item.count) return '0';
      const unitLabel = item.unit === sdlcCategorizationUnits.PIECES ? sdlcCategorizationUnits.PIECES : sdlcCategorizationUnits.CASES;
      return `${item.count} ${unitLabel}`;
    },

    formatEpodCount(fractionalCount) {
      // Convert fractional cases to "X Cases Y Pieces" format
      if (!fractionalCount) return '0 Cases';
      const pcePerBox = this.material && this.material.piece_per_box;
      if (!pcePerBox) return `${fractionalCount} Cases`;

      const cases = Math.floor(fractionalCount);
      const pieces = Math.round((fractionalCount - cases) * pcePerBox);

      if (cases > 0 && pieces > 0) {
        return `${cases} Cases ${pieces} Pieces`;
      } else if (cases > 0) {
        return `${cases} Cases`;
      } else if (pieces > 0) {
        return `${pieces} Pieces`;
      }
      return `${fractionalCount} Cases`;
    },

    validateBeforeSave() {
      const errors = [];
      const hasNewData = Object.keys(this.categorization).some(
        category => this.categorization[category].length > 0,
      );
      if (!hasNewData) {
        errors.push('No categorization data to save');
        return { isValid: false, errors };
      }

      const subTypeCategories = ['shortage', 'damage', 'carton_damage', 'leakage', 'excess'];
      Object.keys(this.categorization).forEach((category) => {
        this.categorization[category].forEach((item, index) => {
          if (!item.category_reason) {
            errors.push(`${category} #${index + 1}: Reason is required`);
          }
          if (subTypeCategories.includes(category) && item.category_reason && !item.probable_cause) {
            errors.push(`${category} #${index + 1}: Probable cause is required`);
          }
          if (subTypeCategories.includes(category) && item.probable_cause === 'Others' && !item.probable_cause_text) {
            errors.push(`${category} #${index + 1}: Please specify the probable cause`);
          }
          if (!item.count || item.count <= 0) {
            errors.push(`${category} #${index + 1}: Count must be greater than zero`);
          }
          if (!item.unit) {
            errors.push(`${category} #${index + 1}: Unit is required`);
          }
        });
      });

      return { isValid: errors.length === 0, errors };
    },


    async handleSave() {
      const validation = this.validateBeforeSave();
      if (!validation.isValid) {
        this.$errorMessage({ message: validation.errors[0] });
        return;
      }

      this.isSaving = true;
      try {
        const payload = {
          order_no: this.orderNo,
          material_ref_id: this.material.material_ref_id,
          material_id: this.material.id,
          material_code: this.material.material.code,
        };

        if (this.material.source_location_id) {
          payload.source_location_id = this.material.source_location_id;
        }
        if (this.material.destination_location_id) {
          payload.destination_location_id = this.material.destination_location_id;
        }
        if (this.material.source_gate_id) {
          payload.source_gate_id = this.material.source_gate_id;
        }
        if (this.material.destination_gate_id) {
          payload.destination_gate_id = this.material.destination_gate_id;
        }
        if (this.material.material_invoice_no) {
          payload.material_invoice_no = this.material.material_invoice_no;
        }
        if (this.material.line_item_no) {
          payload.line_item_no = this.material.line_item_no;
        }

        const transformCategory = (items) => {
          return items.map(item => {
            const transformed = { ...item, count: this.convertToFractionalCases(item) };
            if (transformed.probable_cause_text) {
              transformed.reason = transformed.probable_cause_text;
            }
            delete transformed.probable_cause_text;
            return transformed;
          });
        };

        payload.shortage = transformCategory(this.categorization.shortage);
        payload.damage = transformCategory(this.categorization.damage);
        payload.carton_damage = transformCategory(this.categorization.carton_damage);
        payload.leakage = transformCategory(this.categorization.leakage);
        payload.excess = transformCategory(this.categorization.excess);

        const result = await this.$store.dispatch('order/createSdlcCategorization', { data: payload });
        if (result.error) {
          this.$errorMessage({ message: result.error });
        } else if (result && result.message) {
          this.categorization = {
            shortage: [],
            damage: [],
            carton_damage: [],
            leakage: [],
            excess: [],
          };

          await this.fetchExistingCategorization();

          this.$successMessage({ message: result.message || 'Categorization saved successfully' });

          this.$emit('saved');
        } else {
          this.$errorMessage({ message: 'Failed to save categorization. Please try again.' });
        }
      } catch (error) {
        this.$errorMessage({ message: 'Failed to save categorization. Please try again.' });
      } finally {
        this.isSaving = false;
      }
    },

    handleCancel() {
      this.categorization = {
        shortage: [],
        damage: [],
        carton_damage: [],
        leakage: [],
        excess: [],
      };
      this.activeCategories = [];
      this.isVisible = false;
    },
  },
};
</script>

<style scoped lang="scss">
.sdlc-categorization-popup {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
  }

  .sdlc-popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 10px;

    .sdlc-popup-actions {
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }

  .el-collapse-item__header {
    padding: 10px 10px;
    border-bottom: none;
  }

  .popscroll {
    max-height: 250px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .add_new {
    position: sticky;
    top: 0;
    z-index: 10;
    border: 1px dashed #B5C3CB;
    border-radius: 2px;
    padding: 10px;
    margin: 10px 0;
    color: #1FCD93;
    cursor: pointer;
    font: 1.2rem/14px 'OpenSans-Semibold';
    background: #fff;

    &:hover {
      background-color: #F3F5F6;
      box-shadow: 0 2px 4px 0 #B5C3CB;
    }
  }

  .reason-card {
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    margin: 8px 0;
    padding: 8px;
    background: #f5f7fa;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      background: #f5f7fa;
      border-bottom: 1px solid #e4e7ed;

      .card-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

    &.shortage-card {
      padding: 8px;
      background: #f5f7fa;

      .card-header {
        padding: 4px 4px;
        border-bottom: none;

        .card-title {
          flex: 1;
        }

        .card-actions {
          margin-left: auto;
        }
      }
    }
  }

  .summary-card {
    display: flex;
    align-items: center;
    border-left: 3px solid #7C5CFC;
    padding: 12px 16px;
    margin: 8px 0;
    background: #fff;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
    border-radius: 4px;


    .summary-card-content {
      display: flex;
      gap: 32px;
      flex: 1;
    }

    .summary-field {
      display: flex;
      flex-direction: column;

      &:first-child {
        flex-shrink: 0;
        white-space: nowrap;
      }

      .summary-label {
        font-size: 11px;
        color: #6B7B8A;
        text-transform: uppercase;
        margin-bottom: 4px;
      }

      .summary-value {
        font-size: 14px;
        font-weight: 600;
        color: #2C3E50;
      }
    }
  }
}

.no-gap-form .el-form-item {
  margin-bottom: 0 !important;
}

.count-unit-row {
  display: flex;
  gap: 8px;
  align-items: center;

  .count-input {
    flex: 1;
  }

  .unit-select {
    width: 100px;
  }
}

.epod-count {
  margin-left: auto;
}
</style>
